"use client";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { appUrls } from "@/constants";
import { useAudio } from "@/app/_providers/AudioProvider";
import { Button } from "@components/core/Button";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { Check, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslations } from "next-intl";
import * as m from "motion/react-m";
import { AnimatePresence } from "motion/react";
// TODO: migrate to react aria alternative
import * as RadioGroup from "@radix-ui/react-radio-group";
import clsx from "clsx";
import type { VoiceWithPreview } from "./types";
import { getPreviewUrlForLanguage } from "./types";
import { VoiceAvatar } from "./VoiceAvatar";

interface VoiceListPanelProps {
  displayCategory?: boolean;
  loading?: boolean;
  onContinue?: () => void;
  onPreviewPause?: () => void;
  onPreviewStart?: () => void;
  onVoiceSelect: (voice: VoiceWithPreview) => void;
  pagination?: "show" | "hide" | "on-hover";
  selectedLanguage?: string;
  selectedVoice?: VoiceWithPreview;
  voiceDescriptions?: "show" | "hide";
  voices: VoiceWithPreview[];
}

const VOICES_PER_PAGE = 5;

export function VoiceListPanel({
  displayCategory = false,
  voices,
  selectedVoice,
  onVoiceSelect,
  onContinue,
  loading,
  onPreviewPause,
  onPreviewStart,
  selectedLanguage,
  pagination = "show",
  voiceDescriptions = "show",
}: VoiceListPanelProps) {
  const t = useTranslations("ClientKeys.VoicesWithTTS");
  const selectedIndex = selectedVoice ? voices.findIndex(v => v.voiceId === selectedVoice.voiceId) : -1;
  const initialPage = selectedIndex !== -1 ? Math.floor(selectedIndex / VOICES_PER_PAGE) : 0;
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [direction, setDirection] = useState<"forward" | "backward">("forward");

  const totalPages = Math.ceil(voices.length / VOICES_PER_PAGE);
  // Clamp to valid range so a language switch that reduces voice count never shows an empty page
  const effectivePage = Math.min(currentPage, Math.max(0, totalPages - 1));
  const startIndex = effectivePage * VOICES_PER_PAGE;
  const visibleVoices = voices.slice(startIndex, startIndex + VOICES_PER_PAGE);

  const variants = {
    enter: (direction: "forward" | "backward") => ({
      opacity: 0,
      x: direction === "forward" ? 20 : -20,
    }),
    center: {
      opacity: 1,
      x: 0,
    },
    exit: (direction: "forward" | "backward") => ({
      opacity: 0,
      x: direction === "forward" ? -20 : 20,
    }),
  };

  const handlePrevPage = () => {
    setDirection("backward");
    setCurrentPage(prev => Math.max(0, prev - 1));
    captureEvent(EventName.mw_tts_prev_next_voices, { direction: "previous" });
  };

  const handleNextPage = () => {
    setDirection("forward");
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
    captureEvent(EventName.mw_tts_prev_next_voices, { direction: "next" });
  };

  return (
    <div className='tw-group/panel tw-flex tw-h-full tw-flex-col tw-overflow-hidden'>
      <div className='tw-flex tw-items-center tw-justify-between tw-mb-2 md:tw-mb-3 tw-p-px'>
        <h3 className='tw-opacity-60 f-paragraph-03'>{t("most_popular_voices")}</h3>
      </div>

      <div className='tw-flex-1 -tw-mx-4 tw-px-4 md:-tw-mx-7 md:tw-px-7' id='voice-list'>
        {loading ? (
          <div className='tw-flex tw-h-full tw-items-center tw-justify-center'>
            <div className='f-paragraph-03 text-light'>{t("loading_voices")}</div>
          </div>
        ) : voices.length === 0 ? (
          <div className='tw-flex tw-h-full tw-items-center tw-justify-center'>
            <div className='f-paragraph-03 text-light'>{t("no_voices_available")}</div>
          </div>
        ) : (
          <div className='tw-relative tw-h-full'>
            <AnimatePresence mode='wait' initial={false} custom={direction}>
              <m.div
                key={`page-${effectivePage}`}
                custom={direction}
                variants={variants}
                initial='enter'
                animate='center'
                exit='exit'
                className='tw-h-full'
                transition={{ duration: 0.15, ease: "easeOut" }}
              >
                <RadioGroup.Root
                  value={selectedVoice?.voiceId || ""}
                  className='tw-grid tw-grid-rows-5 tw-h-full'
                  aria-label={t("select_a_voice")}
                >
                  {visibleVoices.map((voice, index) => (
                    <VoiceListItem
                      key={voice.voiceId}
                      voice={voice}
                      index={startIndex + index}
                      displayCategory={displayCategory}
                      isSelected={selectedVoice?.voiceId === voice.voiceId}
                      onPreviewPause={onPreviewPause}
                      onPreviewStart={onPreviewStart}
                      onSelect={() => onVoiceSelect(voice)}
                      selectedLanguage={selectedLanguage}
                      showDescription={voiceDescriptions === "show"}
                    />
                  ))}
                </RadioGroup.Root>
              </m.div>
            </AnimatePresence>
            <div className='tw-sr-only' role='status' aria-live='polite' aria-atomic='true'>
              {t("page_announcement", { current: effectivePage + 1, total: totalPages })}
            </div>
          </div>
        )}
      </div>

      <div className='tw-flex tw-items-center tw-justify-between tw-p-px tw-min-h-10 md:tw-min-h-12 tw-gap-2'>
        <Button
          variant='elevated'
          size='md'
          onClick={onContinue}
          disabled={!selectedVoice}
          className='tw-min-w-0 tw-shrink md:tw-hidden'
        >
          <span className='tw-truncate'>{t("edit_text")}</span>
        </Button>
        <LinkTracked
          asButton
          buttonVariants={{
            variant: "elevated",
            size: "md",
          }}
          className='tw-min-w-0 tw-shrink tw-hidden md:tw-inline-flex'
          href={appUrls.voiceLibrary}
          eventName={EventName.mw_tts_voice_library_explore_clicked}
          eventProperties={{
            source_component: "voices_with_tts_omnibox",
          }}
        >
          <span className='tw-truncate'>{t("explore_voices")}</span>
        </LinkTracked>

        {totalPages > 1 && pagination !== "hide" && (
          <div
            aria-label={t("voice_list_pagination")}
            className={clsx(
              "tw-flex tw-gap-2 tw-shrink-0 tw-transition-opacity tw-duration-200",
              pagination === "on-hover" && "md:tw-opacity-0 md:group-hover/panel:tw-opacity-100",
            )}
          >
            <Button
              variant='elevated'
              size='pill'
              onClick={handlePrevPage}
              disabled={effectivePage === 0}
              aria-label={t("previous_page", { current: effectivePage + 1, total: totalPages })}
              aria-controls='voice-list'
            >
              <ChevronLeft className='tw-size-4 md:tw-size-5' />
            </Button>
            <Button
              variant='elevated'
              size='pill'
              onClick={handleNextPage}
              disabled={effectivePage === totalPages - 1}
              aria-label={t("next_page", { current: effectivePage + 1, total: totalPages })}
              aria-controls='voice-list'
            >
              <ChevronRight className='tw-size-4 md:tw-size-5' />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

interface VoiceListItemProps {
  displayCategory?: boolean;
  index: number;
  isSelected: boolean;
  onPreviewPause?: () => void;
  onPreviewStart?: () => void;
  onSelect: () => void;
  selectedLanguage?: string;
  showDescription?: boolean;
  voice: VoiceWithPreview;
}

function VoiceListItem({
  displayCategory = false,
  voice,
  index,
  isSelected,
  onPreviewPause,
  onPreviewStart,
  onSelect,
  selectedLanguage,
  showDescription = true,
}: VoiceListItemProps) {
  const t = useTranslations("ClientKeys.VoicesWithTTS");
  const { playingId, play, stop } = useAudio();
  const isPlayingPreview = playingId === voice.voiceId;

  const handlePreviewClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isPlayingPreview) {
        stop();
        onPreviewPause?.();
      } else {
        // Track voice preview played (only when manually clicking preview button)
        captureEvent(EventName.mw_tts_voice_heard, {
          source_component: "voices_with_tts_omnibox",
          audio_type: "preview",
          voice_id: voice.voiceId,
          voice_name: voice.name,
          trigger: "preview_button",
        });
        const previewUrl = getPreviewUrlForLanguage(voice, selectedLanguage);
        play(voice.voiceId, previewUrl);
        onPreviewStart?.();
      }
    },
    [isPlayingPreview, play, stop, voice.voiceId, voice.name, onPreviewPause, onPreviewStart, selectedLanguage],
  );

  return (
    <div className='tw-group tw-relative tw-h-full tw-flex tw-items-center tw-min-w-0'>
      {/* Background layer - shows on hover and when selected */}
      <div
        className={clsx(
          "tw-absolute tw-inset-x-0 tw-top-1 tw-bottom-1 tw-rounded-xl tw-transition-colors tw-pointer-events-none",
          "[@media(hover:hover)]:group-hover:tw-bg-black/5",
          isSelected && "tw-bg-black/5",
        )}
      />

      {/* Selection button */}
      <RadioGroup.Item
        value={voice.voiceId}
        className='tw-relative tw-flex tw-w-full tw-items-center tw-gap-3 tw-px-3 tw-py-1 md:tw-py-2 tw-text-left tw-min-w-0 tw-cursor-pointer focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-focus tw-rounded-xl'
        onClick={onSelect}
      >
        <div className='tw-relative tw-size-9 tw-shrink-0'>
          <VoiceAvatar index={index} className='tw-size-9 tw-shrink-0 tw-rounded-full' width={36} height={36} />
          {isSelected && (
            <div className='tw-absolute -tw-bottom-0.5 -tw-right-0.5 tw-size-3.5 tw-rounded-full tw-flex tw-items-center tw-justify-center bg-inverse'>
              <Check className='tw-size-2 text-inverse' strokeWidth={3} />
            </div>
          )}
        </div>

        {/* Voice info - vertically stacked */}
        <div className='tw-min-w-0 tw-flex-1 tw-flex tw-flex-col'>
          <div className='tw-truncate f-paragraph-02'>{voice.name}</div>
          {displayCategory
            ? voice.category && <div className='tw-line-clamp-1 f-paragraph-03 text-light'>{voice.category}</div>
            : voice.description &&
              showDescription && <div className='tw-line-clamp-1 f-paragraph-03 text-light'>{voice.description}</div>}
        </div>

        {/* Spacer for preview button */}
        <div className='tw-size-9 tw-shrink-0' />
      </RadioGroup.Item>

      {/* Preview button - positioned absolutely on the right */}
      <button
        type='button'
        className={clsx(
          "tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2",
          "tw-flex tw-size-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-transition-opacity tw-duration-200",
          "bg-inverse text-inverse",
          // Always visible when playing, selected, on hover, or when parent has focus
          isPlayingPreview || isSelected
            ? "tw-opacity-100"
            : "tw-opacity-0 [@media(hover:hover)]:group-hover:tw-opacity-100 group-focus-within:tw-opacity-100",
        )}
        onClick={handlePreviewClick}
        aria-label={
          isPlayingPreview ? t("pause_preview", { name: voice.name }) : t("play_preview", { name: voice.name })
        }
        aria-pressed={isPlayingPreview}
      >
        {isPlayingPreview ? (
          <Pause className='tw-size-3.5 tw-fill-current' />
        ) : (
          <Play className='tw-size-3.5 tw-fill-current tw-ml-0.5' />
        )}
      </button>
    </div>
  );
}
